import React from 'react';
import { MaybeLayout } from '../components/layout/MaybeLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { DialogTrigger, Dialog } from '../components/ui/dialog';
import { ToggleGroup, ToggleGroupItem } from '../components/ui/toggle-group';
import { useAuth } from '../contexts/AuthContext';
import { MonthYearPicker } from '../components/layout/MonthYearPicker';
import {
  Plus, Calendar as CalendarIcon, ChevronLeft, ChevronRight, Trash2,
  CalendarDays, CalendarRange, LayoutGrid, List,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';

import { useVacations } from './vacations/useVacations';
import VacationCalendarView, { getStatusBadge } from './vacations/VacationCalendarView';
import VacationDialog from './vacations/VacationDialog';

const viewModes = [
  { id: 'day', label: 'Tag', icon: CalendarDays },
  { id: 'week', label: 'Woche', icon: CalendarRange },
  { id: 'month', label: 'Monat', icon: LayoutGrid },
  { id: 'quarter', label: '3 Monate', icon: List },
];

export default function Vacations({ embedded = false }) {
  const { isAdmin, hasPermission } = useAuth();
  const canManageOthers = isAdmin || hasPermission('vacations', 'edit');

  const vm = useVacations({ canManageOthers });

  if (vm.loading) {
    return (
      <MaybeLayout embedded={embedded}>
        <div className="space-y-6">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-[600px] rounded-xl" />
        </div>
      </MaybeLayout>
    );
  }

  return (
    <MaybeLayout embedded={embedded}>
      <div className="space-y-6 animate-fade-in" data-testid="vacations-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="font-heading font-bold text-xl sm:text-3xl text-foreground">Urlaubsplanung</h1>
            <p className="text-muted-foreground mt-1">
              {vm.vacations.filter((v) => v.status === 'approved').length} genehmigte Urlaubstage
            </p>
          </div>
          <Dialog open={vm.dialogOpen} onOpenChange={vm.setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-full" data-testid="new-vacation-btn">
                <Plus className="mr-2 h-4 w-4" />
                {canManageOthers ? 'Urlaub eintragen' : 'Urlaub beantragen'}
              </Button>
            </DialogTrigger>
            <VacationDialog
              open={vm.dialogOpen}
              onOpenChange={vm.setDialogOpen}
              canManageOthers={canManageOthers}
              employees={vm.employees}
              formData={vm.formData}
              setFormData={vm.setFormData}
              selectedRange={vm.selectedRange}
              setSelectedRange={vm.setSelectedRange}
              preview={vm.preview}
              saving={vm.saving}
              onSubmit={vm.handleSubmit}
              holidays={vm.holidays}
              companyFreeDays={vm.companyFreeDays}
              schulferien={vm.schulferien}
            />
          </Dialog>
        </div>

        <div className="grid grid-cols-1 gap-6">
          {/* Calendar View */}
          <Card className="border-border/60">
            <CardHeader className="border-b border-border/40 px-3 sm:px-6">
              <div className="flex flex-col gap-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={vm.navigatePrev} data-testid="prev-btn">
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <CalendarIcon className="h-5 w-5 text-primary hidden sm:block" />
                    <MonthYearPicker currentDate={vm.currentDate} onChange={vm.setCurrentDate} />
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={vm.navigateNext} data-testid="next-btn">
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                  <Button variant="outline" size="sm" onClick={vm.goToToday} className="rounded-full text-xs" data-testid="today-btn">
                    Heute
                  </Button>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <ToggleGroup
                    type="single"
                    value={vm.viewMode}
                    onValueChange={(v) => v && vm.setViewMode(v)}
                    className="border rounded-lg p-0.5"
                  >
                    {viewModes.map((mode) => (
                      <ToggleGroupItem
                        key={mode.id}
                        value={mode.id}
                        aria-label={mode.label}
                        className="data-[state=on]:bg-primary data-[state=on]:text-primary-foreground px-2 sm:px-3 text-xs"
                        data-testid={`view-${mode.id}`}
                      >
                        <mode.icon className="h-3.5 w-3.5 sm:mr-1" />
                        <span className="hidden sm:inline text-xs">{mode.label}</span>
                      </ToggleGroupItem>
                    ))}
                  </ToggleGroup>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-4">
              <VacationCalendarView
                viewMode={vm.viewMode}
                currentDate={vm.currentDate}
                getVacationsForDay={vm.getVacationsForDay}
                getHolidayForDay={vm.getHolidayForDay}
                getSchulferienForDay={vm.getSchulferienForDay}
                isDayInDragRange={vm.isDayInDragRange}
                handleDragStart={vm.handleDragStart}
                handleDragEnter={vm.handleDragEnter}
              />
            </CardContent>
          </Card>
        </div>

        {/* Legend */}
        <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
          <div className="flex items-center gap-2"><div className="w-3 h-3 rounded bg-green-200 dark:bg-green-800" /><span>Genehmigt</span></div>
          <div className="flex items-center gap-2"><div className="w-3 h-3 rounded bg-amber-200 dark:bg-amber-800" /><span>Ausstehend</span></div>
          <div className="flex items-center gap-2"><div className="w-3 h-3 rounded bg-red-200 dark:bg-red-800" /><span>Abgelehnt</span></div>
          <div className="flex items-center gap-2"><div className="w-3 h-3 rounded bg-purple-200 dark:bg-purple-800" /><span>Feiertag</span></div>
          <div className="flex items-center gap-2"><div className="w-3 h-3 rounded bg-amber-200 dark:bg-amber-700" /><span>Schulferien</span></div>
        </div>
      </div>
    </MaybeLayout>
  );
}
